import { useSyncExternalStore } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ColorMode } from "@/theme";

/**
 * What the user chose. `"system"` defers to the OS, which is the state most people expect a
 * modern app to offer and the one that keeps the app in step when the OS flips at sunset.
 *
 * Distinct from `ColorMode`, which is the *resolved* light/dark the theme is actually built from.
 * Everything downstream — `buildTheme`, `theme.shell`, the palettes — only ever sees the resolved
 * value, so nothing else in the app has to know that "system" exists.
 */
export type ColorModePreference = ColorMode | "system";

interface ColorModeState {
  mode: ColorModePreference;
  /** Cycles light → dark → system. Kept because the old topbar button called it. */
  toggle: () => void;
  setMode: (mode: ColorModePreference) => void;
}

const CYCLE: Record<ColorModePreference, ColorModePreference> = {
  light: "dark",
  dark: "system",
  system: "light",
};

export const useColorMode = create<ColorModeState>()(
  persist(
    (set, get) => ({
      // Light is the default on first load; an explicit choice persists and wins. Values already
      // in localStorage are "light" or "dark", both still valid here, so there is nothing to migrate.
      mode: "light",
      toggle: () => set({ mode: CYCLE[get().mode] }),
      setMode: (mode) => set({ mode }),
    }),
    { name: "invmgmt-color-mode" }
  )
);

/* ------------------------------------------------------------------ system preference */

let mediaQuery: MediaQueryList | null = null;
function query(): MediaQueryList | null {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  if (!mediaQuery) mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  return mediaQuery;
}

function subscribeToSystem(onChange: () => void): () => void {
  const m = query();
  if (!m) return () => {};
  m.addEventListener("change", onChange);
  return () => m.removeEventListener("change", onChange);
}

/** Cheap and stable — returns one of two interned strings, so React never sees a new value. */
function systemSnapshot(): ColorMode {
  return query()?.matches ? "dark" : "light";
}

/**
 * The OS colour scheme, live. `useSyncExternalStore` rather than an effect so the very first
 * render already has the right answer — an effect would paint light, then correct itself.
 */
export function useSystemColorMode(): ColorMode {
  return useSyncExternalStore(subscribeToSystem, systemSnapshot, () => "light");
}

/**
 * The light/dark the app should actually render. This is what `buildTheme` takes.
 * Use this everywhere; read the raw store only when you need to show which *option* is selected.
 */
export function useResolvedColorMode(): ColorMode {
  const preference = useColorMode((s) => s.mode);
  const system = useSystemColorMode();
  return preference === "system" ? system : preference;
}
